import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {DialogService} from 'primeng/dynamicdialog';
import {Tooltip} from 'primeng/tooltip';
import {DirectusService} from '@frontend/directus';
import {AppDatePipe, AppNumberPipe} from '@frontend/shared';
import {ToastService} from '@frontend/toast';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ConfirmationDialogService} from '../../dialog/confirmation/confirmation-dialog.service';
import {DirectusTooltipComponent} from '../../directus-tooltip/directus-tooltip.component';
import {
  DocumentUploadCardEdit,
  DocumentUploadCardItem,
  DocumentUploadCardRow,
  DocumentUploadCardRowContext,
  DocumentUploadCardUpload,
  DocumentUploadDialogData,
  DocumentUploadDialogResult,
} from './document-upload-card.types';
import {DocumentUploadDialogComponent} from './document-upload-dialog/document-upload-dialog.component';

const ACCEPTED_EXTENSION = '.pdf';
const MAX_FILE_SIZE_MB = 25;
const INVALID_TYPE_MESSAGE_KEY = 'common.errors.pdfOnly';

@Component({
  selector: 'app-document-upload-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppDatePipe,
    Button,
    AppNumberPipe,
    DirectusTooltipComponent,
    NgTemplateOutlet,
    Tooltip,
    TranslateDirectusPipe,
    TranslocoPipe,
  ],
  templateUrl: './document-upload-card.component.html',
  styleUrl: './document-upload-card.component.scss',
})
export class DocumentUploadCardComponent {
  private readonly toastService = inject(ToastService);
  private readonly dialogService = inject(DialogService);
  private readonly directusService = inject(DirectusService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);

  readonly title = input<string>();
  readonly subtitle = input<string>();
  readonly tooltipKey = input<string>('');
  readonly hint = input<string>();
  readonly isSingleDocument = input<boolean>(false);
  readonly documents = input<DocumentUploadCardItem[]>([]);
  readonly isOptional = input<boolean>(false);
  readonly showStatus = input<boolean>(true);
  readonly canRenameOnEdit = input<boolean>(true);
  readonly readOnly = input<boolean>(false);
  readonly defaultVersion = input<string>('v1.0');
  readonly documentDetails = input<TemplateRef<DocumentUploadCardRowContext>>();
  readonly documentActions = input<TemplateRef<DocumentUploadCardRowContext>>();

  readonly uploadDocument = output<DocumentUploadCardUpload>();
  readonly previewDocument = output<number>();
  readonly downloadDocument = output<number>();
  readonly editDocument = output<DocumentUploadCardEdit>();
  readonly deleteDocument = output<number>();

  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly hasError = signal<boolean>(false);

  private readonly statusOptions = toSignal(
    this.directusService.loadDirectusDropdown('rfq_invitation_upload_document_status'),
    {initialValue: []},
  );

  protected readonly hasDocuments = computed<boolean>(() => this.documents().length > 0);

  protected readonly rows = computed<DocumentUploadCardRow[]>(() => {
    const statusOptions = this.statusOptions();

    return this.documents().map((document) => {
      const status = statusOptions.find((option) => option.id === document.statusUuid);

      return {
        document: document,
        statusTranslations: status?.translations ?? [],
        hasStatus: status !== undefined,
        previewable: document.previewable ?? true,
      };
    });
  });

  protected readonly canUpload = computed<boolean>(() => {
    const isFull: boolean = this.isSingleDocument() && this.hasDocuments();

    return !isFull && !this.readOnly();
  });

  protected onEdit(document: DocumentUploadCardItem): void {
    const data: DocumentUploadDialogData = {
      isEdit: true,
      originalFilename: document.originalFileName,
      fileName: document.fileName,
      version: document.version,
      documentStatusUuid: document.statusUuid,
      visibility: document.visibility,
      showStatus: this.showStatus(),
      isFileNameReadonly: !this.canRenameOnEdit(),
    };
    const ref = this.dialogService.open(DocumentUploadDialogComponent, {
      header: this.title(),
      modal: true,
      closable: true,
      closeOnEscape: true,
      data: data,
    });

    ref?.onClose.subscribe((result: DocumentUploadDialogResult | undefined) => {
      if (result) {
        this.editDocument.emit({
          id: document.id,
          fileName: result.fileName,
          version: result.version,
          statusUuid: result.statusUuid,
          visibility: result.visibility,
        });
      }
    });
  }

  protected onDelete(document: DocumentUploadCardItem): void {
    this.confirmDialogService.confirm({
      headerKey: 'confirmationDialog.deleteItem.header',
      messageKey: 'confirmationDialog.deleteItem.text',
      accept: () => {
        this.deleteDocument.emit(document.id);
      },
    });
  }

  protected triggerUpload(): void {
    this.fileInput().nativeElement.click();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files.item(0);

    if (file) {
      this.processFile(file);
    }
  }

  protected onFileSelected(): void {
    const fileInput = this.fileInput().nativeElement;
    const file = fileInput.files?.item(0);

    if (file) {
      this.processFile(file);
    }

    fileInput.value = '';
  }

  private processFile(file: File): void {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    this.hasError.set(false);

    if (extension !== ACCEPTED_EXTENSION) {
      this.hasError.set(true);
      void this.toastService.showError(INVALID_TYPE_MESSAGE_KEY, INVALID_TYPE_MESSAGE_KEY);
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      this.hasError.set(true);
      void this.toastService.showError('common.errors.fileTooLarge');
      return;
    }

    this.openUploadDialog(file);
  }

  private openUploadDialog(file: File): void {
    const data: DocumentUploadDialogData = {
      file: file,
      showStatus: this.showStatus(),
      defaultVersion: this.defaultVersion(),
    };
    const ref = this.dialogService.open(DocumentUploadDialogComponent, {
      header: this.title(),
      modal: true,
      data: data,
    });

    ref?.onClose.subscribe((result: DocumentUploadDialogResult | undefined) => {
      if (result) {
        this.uploadDocument.emit({
          file: file,
          fileName: result.fileName,
          version: result.version,
          statusUuid: result.statusUuid,
          visibility: result.visibility,
        });
      }
    });
  }
}
